<template>
  <div
    class="bulk-details-cell"
    :class="{ 'bulk-details-cell--column': !alignRight }"
  >
    <div class="bulk-details-cell__content">
      <div
        v-if="title || $slots.title"
        class="bulk-details-cell__title truncate"
      >
        <slot name="title">{{ title }}</slot>
      </div>

      <div class="bulk-details-cell__value">
        <slot :align-right="alignRight">{{ value }}</slot>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from '@/composition/vue/compositionApi'
import { useBreakpoints } from '@/composition/breakpoints'
import type { StylesheetSizeProp } from '@/types'

const { titleMinWidth = '144px', column = undefined } = defineProps<{
  title?: string
  value?: string
  titleMinWidth?: StylesheetSizeProp
  column?: boolean
}>()

const { isMobile } = useBreakpoints()
const alignRight = computed(() => (column !== undefined ? !column : !isMobile.value))
</script>

<style scoped lang="scss">
.bulk-details-cell {
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0 -14px;
    border-radius: $border-radius;
    background-color: $lift;
    opacity: 0;
    pointer-events: none;
  }

  &:hover::before {
    opacity: 0.72;
  }

  &__content {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    flex-grow: 1;
    flex-basis: 0;
    gap: 20px;
    min-height: 44px;
    padding: 10px 0;
    color: $emphasize;
  }

  &__title {
    color: $gray7;
    font-size: $sm-x-font;
    font-weight: $regular-font;
    line-height: 20px;
    white-space: nowrap;
    align-self: self-start;
    max-width: 300px;
    min-width: v-bind(titleMinWidth);
  }

  &__value {
    color: $emphasize;
    font-size: $sm-x-font;
    font-weight: $medium-font;
    line-height: 20px;
    min-width: 0;
    flex: 1;
    display: flex;
    justify-content: flex-end;
    text-align: right;
  }

  &__content > &__value > * {
    min-width: 0;
    width: auto;
  }

  &--column {
    .bulk-details-cell__content {
      padding: 8px 0;
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;
      gap: 4px;
    }

    .bulk-details-cell__value {
      width: 100%;
      justify-content: flex-start;
      text-align: left;
    }

    &::before {
      inset: 0 -10px;
    }
  }
}
</style>
